/**
 * mapTiers.ts — zoom → level-of-detail mapping for the map (T-010, T-098)
 *
 * Single source of truth for the LOD breakpoints so MapShell, the layers, and the
 * tests all agree. Two decisions are derived from the map's zoom:
 *
 *   1. Which geography TIER renders — one of 'state' (dissolved by state, national
 *      zoom-out), 'county' (dissolved county polygons, regional), or 'tract' (zoomed
 *      in). Only one tier is visible/fetched at a time.
 *   2. For the tract tier, which geometry DETAIL to request — 'simplified' at mid zoom
 *      (smaller payload), 'full' at high zoom (precise edges for a specific site).
 *
 * The tier pyramid (T-087 plan §2 tier table):
 *   z<5        → state   (~50 dissolved state polygons; the national default view)
 *   5 ≤ z < 9  → county  (~3,143 dissolved county polygons)
 *   z ≥ 9      → tract   (census tracts; simplified then full-res via detailForZoom)
 *
 * Breakpoints are PROPOSED defaults (O-5) to be tuned in UAT — kept here as named
 * constants, not scattered magic numbers.
 *
 * A block-group/block micro-tier (z >= MICROTIER_MIN_ZOOM) is deliberately NOT wired
 * yet: its data lands in Phase 2.4 P3 (BEAD-0012). tierForZoom never returns it — the
 * constant is here only so the future switch has a documented home (O-6).
 */

export type MapTier = 'state' | 'county' | 'tract'
export type TractDetail = 'simplified' | 'full'

/** Below this zoom the map shows the state tier; at/above it, the county tier (T-098). */
export const STATE_MAX_ZOOM = 5
/** Below this zoom the map shows the county tier; at/above it, the tract tier. */
export const COUNTY_MAX_ZOOM = 9
/**
 * At/above this zoom the tract tier requests full-precision geometry.
 *
 * T-154 (BEAD-0061): lowered 13 → 11. The tract tier's 'simplified' geometry comes from a
 * coarse ST_Simplify(geom, 0.001) at load that keeps only ~28% of the vertices (avg 142 → 40
 * per tract), which reads as blocky/jagged edges when examined at z11–12. Serving full geometry
 * from z11 removes that choppy band at the close-examination zooms; z9–10 (wide regional view,
 * where the simplification is not noticeable and the payload saving matters most) stays simplified.
 * Measured cost of the change at a z11 city viewport: ~+37 KB geometry (~30 → 67 KB), well within
 * bounds. The deeper root-cause fix — a finer, topology-preserving simplification in the loader —
 * needs a tract reload and is tracked separately (see BEAD-0061 notes).
 */
export const TRACT_FULL_ZOOM = 11
/** Reserved for the future BG/block micro-tier (Phase 2.4 P3) — not active. */
export const MICROTIER_MIN_ZOOM = 14

/**
 * Hysteresis band around each tier boundary. A user parked on a boundary who nudges the
 * zoom a hair shouldn't thrash between two full layer fetches, so we require crossing
 * the threshold by this margin (in the current tier's "away" direction) before switching.
 */
export const TIER_HYSTERESIS = 0.25

/**
 * Resolve the geography tier for a zoom level. Pass the previous tier to apply
 * hysteresis (recommended when reacting to live map movement); omit it for a plain
 * threshold decision (e.g. initial render).
 *
 * With a previous tier we resist leaving it until the zoom crosses the relevant boundary
 * by TIER_HYSTERESIS; once we've committed to leaving, the destination tier is chosen by
 * the plain thresholds (so a large jump — e.g. state → tract — still lands correctly).
 */
export function tierForZoom(zoom: number, prev?: MapTier): MapTier {
  if (prev === 'state') {
    // Stay on state until we climb clearly above the state→county boundary.
    if (zoom < STATE_MAX_ZOOM + TIER_HYSTERESIS) return 'state'
    return zoom < COUNTY_MAX_ZOOM ? 'county' : 'tract'
  }
  if (prev === 'tract') {
    // Stay on tracts until we drop clearly below the county→tract boundary.
    if (zoom >= COUNTY_MAX_ZOOM - TIER_HYSTERESIS) return 'tract'
    return zoom < STATE_MAX_ZOOM ? 'state' : 'county'
  }
  if (prev === 'county') {
    // County sits between two boundaries — resist leaving in either direction, EXCEPT there is
    // no downward hysteresis overhang at the state boundary (T-227). The county PMTiles pyramid
    // is built with a minzoom of STATE_MAX_ZOOM (scripts/build-aggregate-tiles.sh: county -Z5),
    // so it has NO tiles below STATE_MAX_ZOOM — retaining the county tier there paints a BLANK
    // band (the state pyramid, which overzooms above z4, is not selected). So drop straight to
    // state at zoom < STATE_MAX_ZOOM. The anti-thrash band is preserved but shifted UP to
    // [STATE_MAX_ZOOM, STATE_MAX_ZOOM + TIER_HYSTERESIS): the state tier is held (overzoomed,
    // still painting) until the zoom climbs clearly past the boundary — see the 'state' branch.
    if (zoom < STATE_MAX_ZOOM) return 'state'
    if (zoom >= COUNTY_MAX_ZOOM + TIER_HYSTERESIS) return 'tract'
    return 'county'
  }
  // No previous tier: plain thresholds.
  if (zoom < STATE_MAX_ZOOM) return 'state'
  if (zoom < COUNTY_MAX_ZOOM) return 'county'
  return 'tract'
}

/**
 * Geometry detail to request for the tract tier at a given zoom.
 *
 * Unlike tierForZoom this is a bare threshold (no hysteresis): a user parked at ~z13 who
 * nudges the zoom can flip detail and re-fetch, but TractLayer's AbortController cancels
 * the stale request so the thrash is cheap and self-correcting. If detail-flip refetching
 * ever shows up as a real cost, apply the same prev-aware hysteresis as tierForZoom.
 */
export function detailForZoom(zoom: number): TractDetail {
  return zoom >= TRACT_FULL_ZOOM ? 'full' : 'simplified'
}
